import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  BarChart3,
  DollarSign,
  X,
  LogOut,
  Shield,
  ExternalLink,
} from 'lucide-react';
import { ROUTES } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';

const sidebarWidth = 16; // 16rem = 256px

const menuItems = [
  { text: 'Ana Sayfa', icon: LayoutDashboard, path: ROUTES.DASHBOARD },
  { text: 'Ürünler', icon: Package, path: ROUTES.PRODUCTS },
  { text: 'Satışlar', icon: ShoppingCart, path: ROUTES.SALES },
  { text: 'Analiz', icon: BarChart3, path: ROUTES.ANALYSIS },
  { text: 'Toplu Güncelleme', icon: DollarSign, path: ROUTES.PRICE_CHANGE_AND_ALERT },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, user } = useAuth();

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose(); // Close sidebar on mobile after navigation
  };

  const handleLogout = async () => {
    await signOut();
    navigate(ROUTES.LOGIN);
    onClose();
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 h-full bg-background-secondary border-r border-border flex flex-col z-50
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        style={{ width: `${sidebarWidth}rem` }}
      >
      {/* Logo/Brand */}
      <div className="relative px-4 lg:px-6 py-4 lg:py-6 flex items-center justify-center min-h-[64px] lg:min-h-[88px]">
        <div className="flex items-center gap-2 -translate-x-3">
          <img
            src="/android-chrome-512x512.png"
            alt="StokFlow"
            className="w-8 h-8 lg:w-10 lg:h-10 flex-shrink-0 rounded-lg object-contain"
          />
          <div className="flex flex-col">
            <h1 className="text-lg lg:text-xl font-semibold text-foreground tracking-tight">
              StokFlow
            </h1>
            <p className="text-xs text-foreground-tertiary mt-1 hidden lg:block">Stok Yönetimi</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="absolute right-2 top-1/2 -translate-y-1/2 lg:hidden p-2 rounded-lg hover:bg-background-tertiary transition-smooth"
          aria-label="Menüyü kapat"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto custom-scrollbar">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <li key={item.text}>
                <button
                  onClick={() => handleNavigate(item.path)}
                  className={`
                    w-full flex items-center gap-2 lg:gap-3 px-3 py-2 lg:py-2.5 rounded-lg
                    text-sm font-medium transition-smooth
                    ${
                      isActive
                        ? 'bg-accent text-white shadow-sm'
                        : 'text-foreground-secondary hover:text-foreground hover:bg-background-tertiary'
                    }
                  `}
                >
                  <Icon className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0" />
                  <span className="truncate">{item.text}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="px-3 lg:px-4 py-3 lg:py-4 border-t border-border">
        <div className="mb-2">
          <a
            href={ROUTES.ADMIN_LOGS}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onClose()}
            className="w-full flex items-center gap-2 lg:gap-3 px-3 py-2 lg:py-2.5 rounded-lg text-sm font-medium transition-smooth text-foreground-secondary hover:text-[#5794f2] hover:bg-[#1a1c22] border border-transparent hover:border-[#2e3742]"
          >
            <Shield className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0" />
            <span className="truncate flex-1 text-left">Admin Panel</span>
            <ExternalLink className="w-3.5 h-3.5 opacity-60 flex-shrink-0" aria-hidden />
          </a>
        </div>
        <div className="mb-3">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 lg:gap-3 px-3 py-2 lg:py-2.5 rounded-lg
              text-sm font-medium transition-smooth
              text-foreground-secondary hover:text-danger hover:bg-danger/10"
          >
            <LogOut className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0" />
            <span className="truncate">Çıkış Yap</span>
          </button>
          
          {user?.email && (
            <div className="mt-2 px-3">
              <p className="text-xs text-foreground-tertiary truncate">
                {user.email}
              </p>
            </div>
          )}
        </div>

        {/* Footer: mobilde sadece marka + yıl, masaüstünde alt satır da */}
        <div className="px-2 py-2">
          <p className="text-xs text-foreground-tertiary text-center">
            ®Ingenium · 2022–{new Date().getFullYear()}
          </p>
          <p className="text-xs text-foreground-tertiary text-center mt-0.5 hidden lg:block">
            Sorun mu var? ne yapmanız gerektiğini biliyorsunuz ;)
          </p>
        </div>
      </div>
    </aside>
    </>
  );
};

export const sidebarWidthClass = `ml-[${sidebarWidth}rem]`;
